import { c as _c } from "react/compiler-runtime";
import type { keepPreviousData } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraphQLClient } from 'graphql-request';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import { graphql } from '../gql/gql.ts';
import type { IndexInput, PrivateEntryInput, SearchResultsInput } from './schema.ts';
import { QueryResponsePrivate, QueryResponsePublished } from './schema.ts';

// @TODO: Move all this logic to a provider, etc.
const getGraphQLClient = () => {
  // TODO secrets

  const {
    NEXT_PUBLIC_SKIPPY_SEARCH_API_URL,
    NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY
  } = getPublicConfig();
  if (!NEXT_PUBLIC_SKIPPY_SEARCH_API_URL || !NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY) {
    throw new Error('Missing environment variable for SKIPPY_SEARCH_API_URL or API_KEY. Please confirm your environment (Vite or Next) set it in your .env file.');
  }
  return new GraphQLClient(NEXT_PUBLIC_SKIPPY_SEARCH_API_URL, {
    headers: {
      'x-api-key': NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY
    } as Record<string, string>
  });
};
const queryKeys = {
  listSearchResults: 'listSearchResults',
  listArticles: 'listArticles',
  listRelatedArticles: 'listRelatedArticles',
  listCatalog: 'listCatalog',
  listPrivateSearchResults: 'listPrivateSearchResults'
};
const indexEntryMutation = graphql(`
  mutation indexEntry($input: IndexInput!) {
    indexEntry(input: $input) {
      success
      error
    }
  }
`);
const indexPrivateEntryMutation = graphql(`
  mutation indexPrivateEntry($input: IndexInput!) {
    indexPrivateEntry(input: $input) {
      success
      error
    }
  }
`);
const removeIndexPrivateEntryMutation = graphql(`
  mutation removeIndexPrivateEntry($input: IndexInput!) {
    removePrivateEntry(input: $input) {
      success
      error
    }
  }
`);
export const publishedFieldsFragment = graphql(`
  fragment PublishedFields on PublishedEntry {
    contentfulEntryID
    contentfulType
    pageTitle
    summary
    searchKeywords
    path
    media {
      image {
        url
        alt
      }
      video {
        url
      }
      caption
    }
    contributors {
      name
      position
      image {
        url
      }
    }
    template
    fund {
      id
      tcmId
      fundTitle
      fundCodeOneTime
      fundCodeCommitment
      catalogAmount
    }
    taxonomy {
      articleType
      articleTopics
      articleThemes
      fundPromotion
      fundCategory
      programCountry
      programRegion
      other
    }
    datePublished
    dateModified
    dateIndexed
  }
`);
export const privateFieldsPageFragment = graphql(`
  fragment PrivateFieldsPage on PrivateEntry {
    pageTitle
    summary
    searchKeywords
    path
  }
`);
export const privateFieldsContentFragment = graphql(`
  fragment PrivateFieldsContent on PrivateEntry {
    title
  }
`);
export const privateFieldsFragment = graphql(`
  fragment PrivateFields on PrivateEntry {
    contentfulEntryID
    contentfulType
    adminTitle
    ...PrivateFieldsPage
    ...PrivateFieldsContent
    isReferenced
    parentPageId
    isOrphaned
    hasChildren
    status
    dateModified
    dateIndexed
  }
`);
const listSearchResultsQuery = graphql(`
  query listSearchResults($input: SearchResultsInput!) {
    listSearchResults(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);
const listArticlesQuery = graphql(`
  query listArticles($input: SearchResultsInput!) {
    listArticles(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      totalCount
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);
const listRelatedArticlesQuery = graphql(`
  query listRelatedArticles($input: SearchResultsInput!) {
    listRelatedArticles(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      totalCount
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);
const listCatalogQuery = graphql(`
  query listCatalog($input: SearchResultsInput!) {
    listCatalog(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);
const listPrivateSearchResultsQuery = graphql(`
  query listPrivateSearchResults($input: SearchResultsInput!) {
    listPrivateSearchResults(input: $input) {
      totalCount
      items {
        ...PrivateFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);
export const getListSearchResultsQuery = async (input: SearchResultsInput) => {
  const graphQLClient = getGraphQLClient();
  const normalizeSearchResults = (data: QueryResponsePublished) => ({
    ...data,
    items: data.items.map(item => ({
      ...item,
      // Find a better way to check this
      fund: item.fund?.fundCodeOneTime === null ? null : item.fund
    }))
  });
  const response = await graphQLClient.request<{
    listSearchResults: QueryResponsePublished;
  }>(listSearchResultsQuery, {
    input
  });
  const stripped = normalizeSearchResults(response.listSearchResults);
  return QueryResponsePublished.parse(stripped);
};
export const useListSearchResultsQuery = (input, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  const normalizeSearchResults = _temp2;
  let t1;
  let t2;
  if ($[1] !== input) {
    t1 = [queryKeys.listSearchResults, input];
    t2 = async () => {
      const response = await graphQLClient.request(listSearchResultsQuery, {
        input
      });
      const stripped = normalizeSearchResults(response.listSearchResults);
      return QueryResponsePublished.parse(stripped);
    };
    $[1] = input;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== options || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      ...options
    };
    $[4] = options;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useListArticlesQuery = (input, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  let t2;
  if ($[1] !== input) {
    t1 = [queryKeys.listArticles, input];
    t2 = async () => {
      const response = await graphQLClient.request(listArticlesQuery, {
        input
      });
      return QueryResponsePublished.parse(response.listArticles);
    };
    $[1] = input;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== options || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      ...options
    };
    $[4] = options;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useListRelatedArticlesQuery = input => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  if ($[1] !== input) {
    t1 = {
      queryKey: [queryKeys.listRelatedArticles, input],
      queryFn: async () => {
        const response = await graphQLClient.request(listRelatedArticlesQuery, {
          input
        });
        return QueryResponsePublished.parse(response.listRelatedArticles);
      }
    };
    $[1] = input;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
export const useListCatalogQuery = input => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  if ($[1] !== input) {
    t1 = {
      queryKey: [queryKeys.listCatalog, input],
      queryFn: async () => {
        const response = await graphQLClient.request(listCatalogQuery, {
          input
        });
        return QueryResponsePublished.parse(response.listCatalog);
      }
    };
    $[1] = input;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
export const useListPrivateSearchResultsQuery = input => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  if ($[1] !== input) {
    t1 = {
      queryKey: [queryKeys.listPrivateSearchResults, input],
      queryFn: async () => {
        const response = await graphQLClient.request(listPrivateSearchResultsQuery, {
          input
        });
        return QueryResponsePrivate.parse(response.listPrivateSearchResults);
      }
    };
    $[1] = input;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
export const useIndexEntryMutation = () => {
  const $ = _c(4);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async input => graphQLClient.request(indexEntryMutation, {
      input
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.listSearchResults]
        });
      }
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useMutation(t2);
};
export const useIndexPrivateEntryMutation = () => {
  const $ = _c(4);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async input => graphQLClient.request(indexPrivateEntryMutation, {
      input
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.listPrivateSearchResults]
        });
      }
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useMutation(t2);
};
export const useDeleteIndexPrivateEntryMutation = () => {
  const $ = _c(4);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async input => graphQLClient.request(removeIndexPrivateEntryMutation, {
      input
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.listPrivateSearchResults],
          exact: false
        });
      }
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useMutation(t2);
};
export const useFetchPrivateChildren = () => {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getGraphQLClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const graphQLClient = t0;
  let t1;
  if ($[1] !== queryClient) {
    const fetchChildren = async parentId => {
      const filters = JSON.stringify({
        "parentPageId.keyword": parentId
      });
      const data = await queryClient.fetchQuery({
        queryKey: [queryKeys.listPrivateSearchResults, parentId],
        queryFn: async () => {
          const res = await graphQLClient.request(listPrivateSearchResultsQuery, {
            input: {
              filters,
              perPage: 1000,
              sort: "path.keyword",
              sortDir: "asc"
            }
          });
          return res?.listPrivateSearchResults?.items as PrivateEntryInput[];
        }
      });
      return data;
    };
    t1 = {
      fetchChildren
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(item) {
  return {
    ...item,
    fund: item.fund?.fundCodeOneTime === null ? null : item.fund
  };
}
function _temp2(data) {
  return {
    ...data,
    items: data.items.map(_temp)
  };
}